import { useEffect, useState } from "react";
import { Facebook, Instagram, Twitter, Youtube, ImageIcon, FileText } from "lucide-react";
import { useLang } from "@/i18n";
import api from "@/api";

export default function Media() {
  const { t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const mediaSrc = (m) => (m.url && m.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${m.url}` : m.url);

  const socials = [
    { name: "Twitter / X", handle: "@samataparty_ss", url: "https://twitter.com/samataparty_ss", icon: Twitter, testid: "media-twitter" },
    { name: "Instagram", handle: "@samataparty_", url: "https://instagram.com/samataparty_", icon: Instagram, testid: "media-instagram" },
    { name: "Facebook", handle: "Samata Party Maharastra", url: "https://www.facebook.com/share/1DYveGmDU9/", icon: Facebook, testid: "media-facebook" },
    { name: "YouTube", handle: "@samatachannel", url: "https://youtube.com/@samatachannel", icon: Youtube, testid: "media-youtube" },
  ];

  return (
    <div data-testid="media-page">
      <section className="bg-emerald-950 text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold" data-testid="media-heading">{t.media_title}</h1>
          <p className="text-amber-400 mt-2 italic">"{t.tagline}"</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
          {socials.map((s) => (
            <a key={s.name} href={s.url} target="_blank" rel="noreferrer" data-testid={`${s.testid}-card`}
              className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-6 hover:shadow-xl hover:-translate-y-1 transition-all group">
              <s.icon className="w-8 h-8 text-emerald-800 group-hover:text-amber-600 transition-colors mb-3" />
              <div className="font-heading font-bold text-emerald-950">{s.name}</div>
              <div className="text-sm text-slate-500">{s.handle}</div>
            </a>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-16 bg-emerald-50 rounded-2xl border border-emerald-100" data-testid="media-empty-state">
            <ImageIcon className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
            <p className="text-slate-500">{t.media_empty}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="media-grid">
            {items.map((m) => (
              <div key={m.id} className="bg-white rounded-2xl border border-emerald-100 shadow-sm overflow-hidden" data-testid={`media-item-${m.id}`}>
                {m.kind === "youtube" ? (
                  <div className="aspect-video">
                    <iframe src={m.url} title={m.title} className="w-full h-full" allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                  </div>
                ) : m.kind === "pdf" ? (
                  <a href={mediaSrc(m)} target="_blank" rel="noreferrer" data-testid={`media-pdf-${m.id}`}
                    className="aspect-video bg-red-50 flex flex-col items-center justify-center gap-3 hover:bg-red-100 transition-colors">
                    <FileText className="w-14 h-14 text-red-600" />
                    <span className="text-sm font-semibold text-red-700">{t.view_pdf}</span>
                  </a>
                ) : m.kind === "video" ? (
                  <video controls className="w-full aspect-video bg-black" data-testid={`media-video-${m.id}`}>
                    <source src={mediaSrc(m)} />
                  </video>
                ) : (
                  <img src={mediaSrc(m)} alt={m.title} className="w-full aspect-video object-cover" />
                )}
                <div className="p-4">
                  <h3 className="font-heading font-semibold text-emerald-950">{m.title}</h3>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
